import { Box, ComponentWithAs, Flex, FlexProps, FormControl, FormLabel, Icon, IconProps, Skeleton, useColorModeValue } from "@chakra-ui/react";
import CDS from "design-system";
import { IconType } from "react-icons";
import ErrorText from "components/FormElements/ErrorText";
import { get } from "lodash";
import { FieldErrors } from "react-hook-form";

interface GreyBlackLabelProps extends FlexProps {
  label: string;
  icon?: IconType | ((props: IconProps) => Element) | ComponentWithAs<"svg", IconProps>;
  labelProps?: object;
  iconProps?: object;
  labelContainerProps?: object;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  additionalComponents?: JSX.Element;
  isInput?: boolean;
}

export interface GreyBlackFieldProps extends GreyBlackLabelProps {
  valueProps?: object;
  isLoading?: boolean;
}

function getSizeProps(size: GreyBlackFieldProps["size"]) {
  let labelFontSize;
  let valueFontSize;
  let boxSize;
  switch (size) {
    case "xs":
      labelFontSize = "2xs";
      valueFontSize = "xs";
      boxSize = "2";
      break;
    case "md":
      labelFontSize = "sm";
      valueFontSize = "md";
      boxSize = "4";
      break;
    case "lg":
      labelFontSize = "md";
      valueFontSize = "lg";
      boxSize = "5";
      break;
    case "xl":
      labelFontSize = "lg";
      valueFontSize = "xl";
      boxSize = "6";
      break;
    default:
      labelFontSize = "xs";
      valueFontSize = "sm";
      boxSize = "3";
      break;
  }
  return {
    labelProps: {
      fontSize: labelFontSize,
    },
    valueProps: {
      fontSize: valueFontSize,
    },
    iconProps: {
      boxSize,
    },
  };
}

export function GreyBlackLabel({
  label,
  icon = undefined,
  size = "sm",
  labelProps = {},
  iconProps = {},
  labelContainerProps = {},
  isInput = true,
  additionalComponents,
}: GreyBlackLabelProps) {
  const { labelProps: sizeLabelProps, iconProps: sizeIconProps } = getSizeProps(size);
  const textColor = "content.tertiary";
  return (
    <Flex align="center" gap="1" {...labelContainerProps}>
      {icon && <Icon {...sizeIconProps} as={icon} {...iconProps} color={textColor} />}
      {isInput ? (
        <FormLabel {...sizeLabelProps} color={textColor} {...labelProps}>{label}</FormLabel>
      ) : (
        <CDS.Text10 {...sizeLabelProps} color={textColor} fontWeight="normal" {...labelProps}>
          {label}
        </CDS.Text10>
      )}
      {additionalComponents}
    </Flex>
  );
}

export default function GreyBlackField({
  label,
  icon = undefined,
  size = "sm",
  isLoading = false,
  valueProps = {},
  labelProps = {},
  iconProps = {},
  labelContainerProps = {},
  additionalComponents,
  children,
  ...rest
}: GreyBlackFieldProps) {
  const valueColor = useColorModeValue("valueBlack.DEFAULT", "var(--chakra-colors-body-text)");
  const { labelProps: sizeLabelProps, valueProps: sizeValueProps, iconProps: sizeIconProps } = getSizeProps(size);
  return (
    <Flex align="flex-start" direction="column" gap=".5" {...rest}>
      <GreyBlackLabel
        {...{ label, icon, size, labelProps: { ...sizeLabelProps, ...labelProps }, iconProps: { ...sizeIconProps, ...iconProps }, labelContainerProps, additionalComponents }}
      />
      {isLoading ? (
        <Skeleton height="1.5rem" width="100%" />
      ) : (
        typeof children === "string" ? (
          <Box as="span" height="7" display="inline-flex" alignItems="center">
            <CDS.Text12 {...sizeValueProps} color={valueColor} fontWeight="normal" lineHeight="1.2" {...valueProps}>
              {children}
            </CDS.Text12>
          </Box>
        ) : (
          children
        )
      )}
    </Flex>
  );
}

interface HorizontalGreyBlackFieldInputProps extends Omit<GreyBlackFieldProps, "additionalComponents"> {
  name: string;
  required?: boolean;
  disabled?: boolean;
  errors: FieldErrors<any>;
  reserveErrorSpace?: boolean;
}

export function HorizontalGreyBlackFieldInput({
  name,
  label,
  errors,
  required = false,
  disabled = false,
  icon = undefined,
  size = "sm",
  isLoading = false,
  labelProps = {},
  valueProps = {},
  iconProps = {},
  labelContainerProps = {},
  reserveErrorSpace = false,
  children,
  ...rest
}: HorizontalGreyBlackFieldInputProps) {
  const valueColor = useColorModeValue("valueBlack.DEFAULT", "var(--chakra-colors-whiteAlpha-900)");
  const { labelProps: sizeLabelProps, valueProps: sizeValueProps, iconProps: sizeIconProps } = getSizeProps(size);
  // Use lodash.get to handle dot-notated names
  const error = get(errors, name);
  return (
    <FormControl variant="unstyled" id={name} isInvalid={!!error} isRequired={required} isDisabled={disabled} {...rest}>
      <Flex align="center" gap="2" {...rest}>
        <GreyBlackLabel
          isInput
          {...{ label, icon, size, labelProps: { ...sizeLabelProps, ...labelProps }, iconProps: { ...sizeIconProps, ...iconProps }, labelContainerProps }}
        />
        {isLoading ? (
          <Skeleton height="1.5rem" width="100%" />
        ) : (
          <Flex direction="column" w="100%">
            {typeof children === "string" ? (
              <CDS.Text12 {...sizeValueProps} color={valueColor} fontWeight="normal" lineHeight="1.2" {...valueProps}>
                {children}
              </CDS.Text12>
            ) : (
              children
            )}
          </Flex>
        )}
      </Flex>
      <ErrorText error={error} reserveErrorSpace={reserveErrorSpace} ml="90px" />
    </FormControl>
  );
}

export function HorizontalGreyBlackField({
  label,
  icon = undefined,
  size = "sm",
  isLoading = false,
  labelProps = {},
  valueProps = {},
  iconProps = {},
  labelContainerProps = {},
  children,
  ...rest
}: Omit<GreyBlackFieldProps, "additionalComponents">) {
  const valueColor = useColorModeValue("valueBlack.DEFAULT", "var(--chakra-colors-whiteAlpha-900)");
  const { labelProps: sizeLabelProps, valueProps: sizeValueProps, iconProps: sizeIconProps } = getSizeProps(size);
  return (
    <Flex align="center" gap="2" {...rest}>
      <GreyBlackLabel
        {...{ label, icon, size, isInput: false, labelProps: { ...sizeLabelProps, ...labelProps }, iconProps: { ...sizeIconProps, ...iconProps }, labelContainerProps }}
      />
      {isLoading ? (
        <Skeleton height="1.5rem" width="100%" />
      ) : (
        typeof children === "string" ? (
          <CDS.Text12 {...sizeValueProps} color={valueColor} fontWeight="normal" lineHeight="1.2" {...valueProps}>
            {children}
          </CDS.Text12>
        ) : (
          children
        )
      )}
    </Flex>
  );
}
